import { useQuery } from '@apollo/client';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Row from 'react-bootstrap/Row';
import { GetVanityPiecesQuery } from '../../../../../../__gqltypes__';
import { GET_VANITY_PIECES } from '../../../../liveops/components/apolloQueries';
import { VANITY_PIECE_TYPE_LABELS } from '../../../../liveops/screens/Items/utils';
import EditableParam from './EditableParam';
import SelectAdd from './SelectAdd';

export type VanityPieceBalance = { id: string; amount: number };

type Props = {
  title: string;
  balance: VanityPieceBalance[];
  onChange: (newBalance: VanityPieceBalance, isNew: boolean) => void;
};

/**
 * Vanity pieces held by the player (spent to unlock PIECES items and badges). Saving an amount of 0 removes it.
 */
export default function VanityPiecesEditor({ title, balance, onChange }: Props) {
  const { data } = useQuery<GetVanityPiecesQuery>(GET_VANITY_PIECES);
  const vanityPieces = data?.liveOps?.vanityPieces ?? [];
  const heldIds = new Set(balance.map(({ id }) => id));

  const addOptions: Record<string, { value: React.ReactNode; searchValue: string }> = {};
  vanityPieces
    .filter(({ id }) => !heldIds.has(id))
    .forEach((piece) => {
      addOptions[piece.id] = {
        value: (
          <>
            {piece.icon && (
              <Image roundedCircle className="img-profile mr-3" src={piece.icon} style={{ width: 20, height: 20 }} />
            )}
            {piece.name}
            {piece.type && <span className="text-muted ml-2">({VANITY_PIECE_TYPE_LABELS[piece.type]})</span>}
          </>
        ),
        searchValue: piece.name,
      };
    });

  return (
    <Form.Group>
      <div className="d-flex justify-content-between align-items-center uv-items-header">
        <div className="uv-items-header-side" />
        <Form.Label className="uv-items-title">{title}</Form.Label>
        <div className="uv-items-header-side d-flex justify-content-end">
          <SelectAdd
            buttonTitle="Add vanity pieces"
            title="Select vanity piece to add"
            // @ts-ignore - SelectAdd renders the option value (icon + name), like MusicFragmentAdd
            options={addOptions}
            onAdd={(id: string) => onChange({ id, amount: 0 }, true)}
          />
        </div>
      </div>
      {balance.length > 0 ? (
        <Row>
          {balance.map((pieceBalance) => {
            const piece = vanityPieces.find(({ id }) => id === pieceBalance.id);
            return (
              <Col key={pieceBalance.id} md="auto" className="mb-4">
                <Card>
                  <Card.Header>
                    {piece?.icon && (
                      <Image
                        roundedCircle
                        className="img-profile mr-3"
                        src={piece.icon}
                        style={{ width: 30, height: 30 }}
                      />
                    )}
                    {piece?.name ?? pieceBalance.id}
                    {piece?.type && (
                      <span className="text-muted ml-2">({VANITY_PIECE_TYPE_LABELS[piece.type]})</span>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <EditableParam
                      key="amount"
                      title="amount"
                      value={pieceBalance.amount ?? undefined}
                      // @ts-ignore
                      onChange={(event) => onChange({ ...pieceBalance, amount: event.target.value }, false)}
                      placeholder={`${pieceBalance.amount}`}
                    />
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <p>
          <i>No vanity pieces</i>
        </p>
      )}
    </Form.Group>
  );
}
